import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { SendIcon, StopIcon } from './icons.tsx';

const MAX_LENGTH = 2000;

export function Composer({
  busy,
  onSend,
  onStop,
}: {
  busy: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
}) {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (busy || !text.trim()) return;
    onSend(text);
    setText('');
    // Keep the keyboard closed on mobile after sending: the answer needs the screen
    ref.current?.blur();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) submit(e);
  };

  return (
    <form onSubmit={submit} className="mx-auto flex max-w-2xl items-end gap-2 px-4 py-2">
      <label htmlFor="question" className="sr-only">
        Tu pregunta
      </label>
      <textarea
        id="question"
        ref={ref}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        rows={1}
        maxLength={MAX_LENGTH}
        enterKeyHint="send"
        placeholder="Escribe tu pregunta…"
        className="field-sizing-content max-h-36 min-h-12 flex-1 resize-none rounded-3xl border-2 border-purple/30 bg-white px-4 py-2.5 text-lg text-ink placeholder:text-ink-soft/70 focus:border-purple focus:outline-none dark:border-cream/25 dark:bg-night-700 dark:text-cream dark:placeholder:text-cream/50 dark:focus:border-gold-300"
      />
      {busy ? (
        <button
          type="button"
          onClick={onStop}
          aria-label="Detener la respuesta"
          className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-ink-soft text-cream dark:bg-cream/80 dark:text-ink"
        >
          <StopIcon />
        </button>
      ) : (
        <button
          type="submit"
          aria-label="Enviar pregunta"
          disabled={!text.trim()}
          className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-purple text-cream disabled:opacity-40 dark:bg-gold-300 dark:text-ink"
        >
          <SendIcon />
        </button>
      )}
    </form>
  );
}
